import { isEqual, isNull } from 'lodash/fp';

import { ApplicationState } from '../../../../store/types';
import { PdfPageText, PdfSearchResults, PdfSearchSettings, ControlBarView, PDFPageHighlights, DocumentViewerType } from './types';
import { getPdfRoot } from '../../../../store/rootSelector';

export const getPdfText = (state: ApplicationState): PdfPageText[] => getPdfRoot(state).pdfText;

export const getPdfSearchTerm = (state: ApplicationState): string => getPdfRoot(state).pdfSearchTerm;

export const getCurrentPdfPage = (state: ApplicationState): number => getPdfRoot(state).currentPdfPage;

export const getSearchResults = (state: ApplicationState): PdfSearchResults[] => getPdfRoot(state).searchResults;

export const getScrollToInitialSearchResults = (state: ApplicationState): boolean => getPdfRoot(state).scrollToInitialResult;

export const getSelectedSearchResult = (state: ApplicationState): PdfSearchResults | null => getPdfRoot(state).selectedSearchResult;

export const getSearchSettings = (state: ApplicationState): PdfSearchSettings => getPdfRoot(state).searchSettings;

export const getScheduleStartPage = (state: ApplicationState): number => getPdfRoot(state).scheduleStartPage;

export const getCanApplyChanges = (state: ApplicationState): boolean => {
    const currentSettings = getPdfRoot(state).searchSettings;
    const savedSearchSettings = getPdfRoot(state).savedSearchSettings;
    return !isEqual(currentSettings, savedSearchSettings);
};

export const getIndexOfCurrentlySelected = (state: ApplicationState): number | null => {
    const selectedSearchResult = getSelectedSearchResult(state);
    const searchResults = getSearchResults(state);
    return isNull(selectedSearchResult) ? null : searchResults.findIndex(result => result.page === selectedSearchResult.page && selectedSearchResult.indexes.every(index => result.indexes.includes(index)));
};

export const getCanNextHighlight = (state: ApplicationState) => {
    const searchResults = getSearchResults(state);
    const currentPage = getCurrentPdfPage(state);
    const resultsAfterCurrentPage = searchResults.filter(({ page }) => page >= currentPage).sort((a, b) => a.page - b.page);
    const currentlySelectedIndex = getIndexOfCurrentlySelected(state);
    if (isNull(currentlySelectedIndex)) {
        return resultsAfterCurrentPage.length > 0;
    }
    return currentlySelectedIndex + 1 < searchResults.length;
};

export const getCanPreviousHighlight = (state: ApplicationState) => {
    const searchResults = getSearchResults(state);
    const currentPage = getCurrentPdfPage(state);
    const resultsAfterCurrentPage = searchResults.filter(({ page }) => page < currentPage).sort((a, b) => b.page - a.page);
    const currentlySelectedIndex = getIndexOfCurrentlySelected(state);
    if (isNull(currentlySelectedIndex)) {
        return resultsAfterCurrentPage.length > 0;
    }
    return currentlySelectedIndex - 1 >= 0;
};

export const getControlBarView = (state: ApplicationState): ControlBarView => getPdfRoot(state).controlBarView;

export const getNumberOfPages = (state: ApplicationState): number | null => getPdfRoot(state).numberOfPages;

export const getPageHighlightInfo = (state: ApplicationState): PDFPageHighlights | undefined => getPdfRoot(state).pageHighlightInfo;

export const getIsCheckingForHighlights = (state: ApplicationState): boolean => getPdfRoot(state).checkingPageHighlights;

export const getDocumentViewerType = (state: ApplicationState): DocumentViewerType | null => getPdfRoot(state).type;
